// Implements: F-UIX-020, F-DSG-002, F-DSG-003, F-DSG-005, F-DSG-008
/**
 * The closed vocabularies of a profile version and its contrast pairs.
 *
 * Copies of the backend's constants (`font_stack.py`, `type_scale.py`,
 * `register.py`, `density.py`, `radius.py`, `palette.py`), so the editor offers
 * closed selects and never a free value. The server re-validates every one of
 * them after access: a copy that drifted would be refused, never stored.
 *
 * Mockup v3 P5: the eleven Google families (contract 1.1, with the weights each one is served in,
 * checked against the Google Fonts catalogue), the 12 px corner (`rounded`; the 16 px `large` stays
 * readable for stored versions but is no longer offered) and the secondary colour with its pair.
 */

import type { PaletteRole, ProfileVersionValues } from "../../../types/design";

export const SYSTEM_FONT_STACKS = ["system-sans", "system-serif", "system-rounded", "system-mono"] as const;
export const GOOGLE_FONT_STACKS = [
  "inter",
  "poppins",
  "manrope",
  "dm-sans",
  "montserrat",
  "roboto",
  "lato",
  "source-serif-4",
  "playfair-display",
  "merriweather",
  "ibm-plex-mono",
] as const;
export const FONT_STACKS = [...SYSTEM_FONT_STACKS, ...GOOGLE_FONT_STACKS] as const;
export type FontStack = (typeof FONT_STACKS)[number];
export type GoogleFont = (typeof GOOGLE_FONT_STACKS)[number];

export interface GoogleFamily {
  /** The family name Google serves, also the first name of its CSS stack. */
  readonly family: string;
  readonly category: "sans" | "serif" | "mono";
  /** The weights requested; only ones the catalogue serves, or the css2 call answers 400. */
  readonly weights: readonly number[];
}

const BODY_WEIGHTS = [400, 500, 600, 700] as const;

export const GOOGLE_FONTS: Readonly<Record<GoogleFont, GoogleFamily>> = {
  inter: { family: "Inter", category: "sans", weights: BODY_WEIGHTS },
  poppins: { family: "Poppins", category: "sans", weights: BODY_WEIGHTS },
  manrope: { family: "Manrope", category: "sans", weights: BODY_WEIGHTS },
  "dm-sans": { family: "DM Sans", category: "sans", weights: BODY_WEIGHTS },
  montserrat: { family: "Montserrat", category: "sans", weights: BODY_WEIGHTS },
  roboto: { family: "Roboto", category: "sans", weights: [400, 500, 700] },
  lato: { family: "Lato", category: "sans", weights: [400, 700] },
  "source-serif-4": { family: "Source Serif 4", category: "serif", weights: [400, 600, 700] },
  "playfair-display": { family: "Playfair Display", category: "serif", weights: [400, 600, 700] },
  merriweather: { family: "Merriweather", category: "serif", weights: [400, 700] },
  "ibm-plex-mono": { family: "IBM Plex Mono", category: "mono", weights: BODY_WEIGHTS },
};

/** Whether a stored or chosen font literal is one of the Google families. */
export function isGoogleFont(font: string): font is GoogleFont {
  return (GOOGLE_FONT_STACKS as readonly string[]).includes(font);
}

/** The only origin a font stylesheet is ever loaded from (the SPA CSP allows it, default P5-2). */
export const GOOGLE_FONTS_CSS2 = "https://fonts.googleapis.com/css2";

/**
 * The stylesheet of the Google families among `fonts` (titles then text, each family once), built
 * from the code table only, as the backend's `google_fonts_stylesheet`; `null` when none is Google.
 */
export function googleFontsHref(fonts: readonly string[]): string | null {
  const families = [...new Set(fonts.filter(isGoogleFont))].map((font) => {
    const { family, weights } = GOOGLE_FONTS[font];
    return `family=${family.replaceAll(" ", "+")}:wght@${weights.join(";")}`;
  });
  return families.length === 0 ? null : `${GOOGLE_FONTS_CSS2}?${families.join("&")}&display=swap`;
}

/** One stylesheet to load: a Google family and its own css2 address. */
export interface GoogleFontLink {
  readonly font: GoogleFont;
  readonly href: string;
}

/** One stylesheet per Google family among `fonts` (each once), so a family is loaded only when shown. */
export function googleFontLinks(fonts: readonly string[]): readonly GoogleFontLink[] {
  return [...new Set(fonts.filter(isGoogleFont))].map((font) => ({ font, href: googleFontsHref([font]) ?? "" }));
}

export const BASE_SIZES = [14, 15, 16, 17, 18] as const;
export const TYPE_SCALES = [1.2, 1.25, 1.333] as const;
export const REGISTERS = ["minimal", "dense", "editorial", "corporate"] as const;
export const DENSITIES = ["compact", "regular", "comfortable"] as const;
/** Every radius a stored version may carry; `large` (16 px) only from before mockup v3. */
export const RADII = ["none", "small", "medium", "rounded", "large"] as const;
/** The corners the editor offers: 0, 4, 8 and 12 px. */
export const OFFERED_RADII = ["none", "small", "medium", "rounded"] as const;

export const PALETTE_ROLES: readonly PaletteRole[] = [
  "primary",
  "secondary",
  "surface",
  "text",
  "success",
  "warning",
  "danger",
];

/** The server's code-authored pair names, the only thing a contrast refusal carries. */
export type PairName =
  | "text-on-surface"
  | "surface-on-primary"
  | "text-on-secondary"
  | "success-on-surface"
  | "warning-on-surface"
  | "danger-on-surface";

export interface ContrastPairDefinition {
  readonly name: PairName;
  readonly foreground: PaletteRole;
  readonly background: PaletteRole;
}

/** `CONTRAST_PAIRS`, in the server's order; `(primary, surface)` is the same ratio and not repeated. */
export const CONTRAST_PAIRS: readonly ContrastPairDefinition[] = [
  { name: "text-on-surface", foreground: "text", background: "surface" },
  { name: "surface-on-primary", foreground: "surface", background: "primary" },
  { name: "text-on-secondary", foreground: "text", background: "secondary" },
  { name: "success-on-surface", foreground: "success", background: "surface" },
  { name: "warning-on-surface", foreground: "warning", background: "surface" },
  { name: "danger-on-surface", foreground: "danger", background: "surface" },
];

/**
 * The value set of a stored version, without its identity or its compiled tokens.
 *
 * A new profile starts from the values of the system default the server
 * serves (`GET /design/profiles/{system_default.id}`), never from a copy here:
 * a starting point only, not a preset (P6-OD-18). A version stored before the
 * secondary colour existed (six roles) opens with `secondary = surface`, the
 * server's own backfill, which already passes `text-on-surface`.
 */
export function valuesOf(version: ProfileVersionValues): ProfileVersionValues {
  const palette: Partial<ProfileVersionValues["palette"]> = version.palette;
  return {
    heading_font: version.heading_font,
    body_font: version.body_font,
    base_size: version.base_size,
    scale: version.scale,
    palette: { ...version.palette, secondary: palette.secondary ?? version.palette.surface },
    register: version.register,
    density: version.density,
    radius: version.radius,
    instructions: version.instructions,
  };
}

/**
 * Whether the editor is offered for a profile (P6-OD-16): exactly when the
 * server says the person may edit it (`may_edit`, its own `policy.may_edit`),
 * so team and organization profiles open for their editors and the client
 * knows no role. The system default stays read-only whatever it carries
 * (P6-OD-18). The server still decides every save.
 */
export function isEditable(profile: {
  readonly is_system_default: boolean;
  readonly may_edit: boolean;
}): boolean {
  return !profile.is_system_default && profile.may_edit;
}

/** The pairs a server message names, in declaration order (the message carries names only). */
export function pairsNamedIn(message: string): readonly PairName[] {
  return CONTRAST_PAIRS.map((pair) => pair.name).filter((name) => message.includes(name));
}

